import createQuoteLineId from "../../lib/createQuoteLineId";
import PaymentPanel from "../../components/Payments/PaymentPanel";
import { useNavigate, useParams } from "react-router-dom"
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import { Autocomplete, Box, Button, CircularProgress, IconButton, Modal, Tab, Tabs, TextField, Typography, useMediaQuery } from "@mui/material"
import { Category, Close, Description, Handyman, Info, LocalShipping, Tune } from "@mui/icons-material"
import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../lib/api";
import SoldTo from "./SoldTo";
import SaleInfo from "./SaleInfo";
import Notes from "./Notes";
import Custom from "./Custom";
import QuoteItems from "./QuoteItems";
import Tools from "./Tools";
import Feedback from "../../components/Feedback/Feedback";



const isIndex = (s) => /^\d+$/.test(String(s));

function setDeep(obj, path, value) {
  const keys = path.split(".");
  const next = structuredClone(obj ?? {});
  let cur = next;

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const nextK = keys[i + 1];

    if (isIndex(k)) {
      const idx = Number(k);
      if (!Array.isArray(cur)) throw new Error(`Expected array at ${keys.slice(0, i).join(".")}`);
      if (cur[idx] == null) cur[idx] = isIndex(nextK) ? [] : {};
      cur = cur[idx];
    } else {
      if (cur[k] == null) cur[k] = isIndex(nextK) ? [] : {};
      cur = cur[k];
    }
  }

  const last = keys[keys.length - 1];
  if (isIndex(last)) {
    if (!Array.isArray(cur)) throw new Error(`Expected array at ${keys.slice(0, -1).join(".")}`);
    cur[Number(last)] = value;
  } else {
    cur[last] = value;
  }

  return next;
}

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            className="hive-base-unique-quote-tab-panel"
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const EmbeddedQuote = ({ content }) => content;

const UniqueQuote = ({ quoteId, projectNumber, embedded = false, onRevisionCreated, onSaved }) => {
    const [value, setValue] = useState(0);
    const [quote,setQuote] = useState({})
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [copyModal, setCopyModal] = useState(false)
    const [copyTargetOpportunity, setCopyTargetOpportunity] = useState(null)
    const [copyOpportunityOptions, setCopyOpportunityOptions] = useState([])
    const [copyOpportunitiesLoading, setCopyOpportunitiesLoading] = useState(false)
    const [feedback, setFeedback] = useState({}) 
    const [drawingModal, setDrawingModal] = useState(false)
    const [drawingPdf, setDrawingPdf] = useState(null);
    const [drawingPdfError, setDrawingPdfError] = useState("");    
    const { qid: routeQuoteId } = useParams()
    const qid = quoteId || routeQuoteId;
    const Wrapper = embedded ? EmbeddedQuote : BaseWrapper;
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;
    useEffect(() => {
        if (embedded && projectNumber !== undefined) {
            setQuote(previous => ({ ...previous, projectNumber: projectNumber || '' }));
        }
    }, [embedded, projectNumber]);
    const navigate = useNavigate();

    const readOnly = quote?.canEdit === false;

    const isMobile = useMediaQuery('(max-width:600px)');
    const isLaptop = useMediaQuery('(max-width:1280px)')

    const handleChange = (event, newValue) => setValue(newValue);

    // ---- Debounce infrastructure ----
    const debounceTimerRef = useRef(null);
    const pendingPatchRef = useRef({});   // accumulates changes
    const abortRef = useRef(null);
    const activeSavePromiseRef = useRef(null);
    const saveSeqRef = useRef(0);      // increments per save
    const latestAppliedSeqRef = useRef(0);

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '65vw' : isLaptop ? '40vw' : '30vw',
        maxHeight: 750,
        overflow: "hidden",
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
    };
  
    const headerFooterStyle = {
        position: 'sticky',
        zIndex: 1,
        backgroundColor: 'background.paper',
    };     

    const handleRevision = async () => {
        await flushQuotePatch();
        const response = await API.Quotes.CreateRevision(qid, { "createdBy": localStorage.id })
        if (response.status === 200) {
            if (embedded && onRevisionCreated) onRevisionCreated(response?.data?._id);
            else navigate(`/base-unique-quote/${response?.data?._id}`)
            setFeedback({open: true, message: "Revision Successfully Created! Data Refreshed", type: "success"})
        }
        else {
            setFeedback({open: true, message: "Failed to create rev...", type: "error"})
        }
    }

    const handleDeliver = async () => {
        await flushQuotePatch();
        const res = await API.Quotes.DeliveryPDF(qid);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber  ? `${quote.quoteNumber}.pdf` : `quote-${qid}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };

    const handleDeliverNoPricing = async () => {
        await flushQuotePatch();
        const res = await API.Quotes.DeliveryPDFNoPricing(qid);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber ? `${quote.quoteNumber}-no-pricing.pdf` : `quote-${qid}-no-pricing.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };

    const handleDeliverConfirmation = async () => {
        await flushQuotePatch();
        const res = await API.Quotes.DeliveryOrderConfirmation(qid);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber  ? `${quote.projectNumber} Order Confirmation.pdf` : `Order Confirmation.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };      
    
    const loadCopyOpportunityOptions = async () => {
        setCopyOpportunitiesLoading(true)

        try {
            const opportunities = await API.Opportunities.FetchAll()
            const currentOpportunityId = String(quote?.opportunityId?._id || quote?.opportunityId || "")
            setCopyOpportunityOptions((opportunities || [])
                .filter(opportunity => String(opportunity._id) !== currentOpportunityId && !opportunity.quoteId)
                .map(opportunity => ({
                    value: opportunity._id,
                    label: opportunity.name || opportunity.opportunityNumber || String(opportunity._id),
                })))
        } catch (error) {
            console.error("Failed to load copy opportunities:", error)
            setCopyOpportunityOptions([])
            setFeedback({ open: true, message: "Failed to load opportunities for quote copy.", type: "error" })
        } finally {
            setCopyOpportunitiesLoading(false)
        }
    }

    const handleCopyQuote = async () => {
        if (!copyTargetOpportunity?.value) {
            setFeedback({ open: true, message: "Select an opportunity before copying this quote.", type: "error" })
            return
        }

        const response = await API.Quotes.Copy(qid, {
            opportunityId: copyTargetOpportunity.value,
        })

        if (response.status === 200) {
            navigate(`/base-unique-quote/${response?.data?._id}`)
            setFeedback({open: true, message: "Quote Successfully Copied! Data Refreshed", type: "success"})
            handleClose()
        }
        else {
            setFeedback({open: true, message: response?.data?.message || "Failed to copy quote...", type: "error"})
        }
    }

    const handleOpenCopyModal = async () => {
        setCopyModal(true)
        setCopyTargetOpportunity(null)
        await loadCopyOpportunityOptions()
    }
    const handleClose = () => {
        setCopyModal(false)
        setCopyTargetOpportunity(null)
    }

    const handleRefreshPricing = async () => {
        const response = await API.Quotes.RefreshPricing(qid);
        setLoading(true)

        if (response.status === 200) {
            setQuote(response.data.quote); // update local state
            setFeedback({
                open: true,
                message: "Pricing refreshed successfully!",
                type: "success"
            });
        } else {
            setFeedback({
                open: true,
                message: "Failed to refresh pricing...",
                type: "error"
            });
        }        
        setLoading(false)
    }

    const removeTag = async (tagToRemove) => {
        // 1) compute next tags from current quote
        const updatedTags = (quote?.tags || []).filter(t => t !== tagToRemove);

        // 2) optimistic UI update
        setQuote(prev => ({
            ...prev,
            tags: updatedTags
        }));

        // 3) API (later)
        patchQuote({ tags: updatedTags });
    };  

    const handleOpenDrawingModal = () => setDrawingModal(true)    
    const handleCloseDrawingModal = () => {
        setDrawingModal(false)
        // Remove file from state
        handleRemoveDrawingPdf()
    }

    const handleSelectDrawingPdf = (e) => {
        const file = e.target.files?.[0] || null;

        // user canceled file picker
        if (!file) return;

        // validate type (some browsers may give empty type, so also check extension)
        const isPdf =
            file.type === "application/pdf" ||
            file.name.toLowerCase().endsWith(".pdf");

        if (!isPdf) {
            setDrawingPdf(null);
            setDrawingPdfError("Please select a PDF file.");
            // reset input so selecting the same bad file still triggers onChange
            e.target.value = "";
            return;
        }

        setDrawingPdf(file);
        setDrawingPdfError("");
    };

    const handleRemoveDrawingPdf = () => {
        setDrawingPdf(null);
        setDrawingPdfError("");
    };    
    const handlePrintWithDrawing = async () => {
        if (!drawingPdf) {
            setDrawingPdfError("Attach a PDF before submitting.");
            return;
        }

        await flushQuotePatch();
        const res = await API.Quotes.DeliverPDFWithDrawing(qid, drawingPdf);

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber
            ? `${quote.quoteNumber}.pdf`
            : `quote-${qid}-with-drawing.pdf`;

        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
        handleCloseDrawingModal()
    };

    const saveQuotePatch = useCallback(async (payload) => {
        if (!payload || !Object.keys(payload).length) {
            return null;
        }

        const ops = Object.entries(payload).map(([path, value]) => ({ path, value }));
        const mySeq = ++saveSeqRef.current;
        let savePromise = null;

        try {
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();

            savePromise = API.Quotes.Update(qid, { ops }, {
                signal: abortRef.current.signal,
            });

            activeSavePromiseRef.current = savePromise;

            const updated = await savePromise;

            if (mySeq < saveSeqRef.current) {
                return updated?.data ?? null;
            }

            latestAppliedSeqRef.current = mySeq;

            setQuote((prev) => {
                const hasPending = Object.keys(pendingPatchRef.current).length > 0;
                if (hasPending) return prev;
                return updated?.data ?? prev;
            });

            onSavedRef.current?.();
            return updated?.data ?? null;
        } catch (err) {
            if (err?.name === "AbortError") return null;
            console.error("Failed to autosave quote:", err);
            return null;
        } finally {
            if (activeSavePromiseRef.current === savePromise) {
                activeSavePromiseRef.current = null;
            }
        }
    }, [qid]);

    const patchQuote = useCallback((partial) => {
    // 1) update local state immediately (supports dot paths)
    setQuote((prev) => {
        let next = prev;

        for (const [key, val] of Object.entries(partial)) {
        if (key.includes(".")) {
            next = setDeep(next, key, val);
        } else {
            next = { ...next, [key]: val };
        }
        }

        return next;
    });

    // 2) accumulate patches so multiple fields typed quickly become ONE request
    pendingPatchRef.current = { ...pendingPatchRef.current, ...partial };

    // 3) debounce server call
    clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(async () => {
        const payload = pendingPatchRef.current;
        pendingPatchRef.current = {};
        await saveQuotePatch(payload);
    }, 600);
    }, [saveQuotePatch, setQuote]);
  

    const addProductsToQuote = useCallback((selectedProducts) => {
        const lines = Array.isArray(quote?.lines) ? quote.lines : [];

        const maxLineNumber =
            lines.length === 0 ? 0 : Math.max(...lines.map(l => Number(l.lineNumber) || 0));

        const newLines = (selectedProducts || []).map((p, idx) => {
            const qty = 1;
            const cost = Number(p.cost) || 0;
            const price = Number(p.price) || 0;

            return {
                _id: createQuoteLineId(),
            lineNumber: maxLineNumber + idx + 1,
            type: "item",
            productId: p._id,

            manufacturerPartNumber: p.manufacturerPartNumber || "",
            descriptionTitle: p.description || "",
            descriptionBody: p.descriptionBody || "",

            quantity: qty,
            cost,
            extCost: Number((qty * cost).toFixed(2)),
            price,
            extPrice: Number((qty * price).toFixed(2)),

            isSubtotal: false,
            isDiscount: false,
            };
        });

        // Structural op: OK to set whole lines array
        patchQuote({ lines: [...lines, ...newLines] });
    }, [quote?.lines, patchQuote]);    

    const flushQuotePatch = useCallback(async () => {
        clearTimeout(debounceTimerRef.current);
        const payload = pendingPatchRef.current;
        pendingPatchRef.current = {};
        if (Object.keys(payload).length) {
            return await saveQuotePatch(payload);
        }

        if (activeSavePromiseRef.current) {
            return await activeSavePromiseRef.current;
        }

        return null;
    }, [saveQuotePatch]);    

    // Save pending edits when leaving the quote; opportunity tabs keep it mounted.
    useEffect(() => {
        return () => {
            clearTimeout(debounceTimerRef.current);
            const payload = pendingPatchRef.current;
            pendingPatchRef.current = {};
            if (Object.keys(payload).length) void saveQuotePatch(payload);
        };
    }, [saveQuotePatch]);

    useEffect(() => {   
        async function fetchData() {
            const [quoteResponse, usersResponse] = await Promise.all([
                API.Quotes.FetchOne(qid),
                API.Users.FetchAll(),
            ])

            setQuote(quoteResponse)
            setUsers(usersResponse.map((userRecord) => ({
                value: userRecord._id,
                label: `${userRecord.firstName} ${userRecord.lastName}`,
            })))
            setLoading(false)
        }
        fetchData()
    },[qid])

    return (
        <Wrapper content={
            loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>   
            ) : (            
                <Box className="hive-base-unique-quote" sx={{ width: '100%' }}>
                    <Feedback props={feedback} setFeedback={setFeedback} />
                    {!embedded && (quote?.opportunityId ? <Button href={"/opportunities/" + (quote.opportunityId._id || quote.opportunityId)}>Manage revenue-line payments</Button> : <PaymentPanel readOnly={readOnly} quoteId={qid} version={quote?.updatedAt} />)}

                    <Box className="hive-base-unique-quote-tabs-shell" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            className="hive-base-unique-quote-tabs"
                            value={value}
                            onChange={handleChange}
                            aria-label="basic tabs example"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
                            }}                        
                        >
                            <Tab className="hive-base-unique-quote-tab" icon={<Category />} label="Items" {...a11yProps(0)} />
                            <Tab className="hive-base-unique-quote-tab" icon={<LocalShipping />} label="Sold-To/Ship-To" {...a11yProps(1)} />
                            <Tab className="hive-base-unique-quote-tab" icon={<Info />} label="Sale Info" {...a11yProps(2)} />
                            <Tab className="hive-base-unique-quote-tab" icon={<Description />} label="Notes" {...a11yProps(3)} />
                            <Tab className="hive-base-unique-quote-tab" icon={<Tune />} label="Custom" {...a11yProps(4)} />
                            <Tab className="hive-base-unique-quote-tab" icon={<Handyman />} label="Tools" {...a11yProps(5)} />
                        </Tabs>
                    </Box>     

                    <CustomTabPanel value={value} index={0}>
                        <QuoteItems readOnly={readOnly} quote={quote} patchQuote={patchQuote} onAddProducts={addProductsToQuote} />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={1}>
                        <SoldTo readOnly={readOnly} quote={quote} patchQuote={patchQuote} flushQuotePatch={flushQuotePatch} />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={2}>
                        <SaleInfo readOnly={readOnly} quote={quote} patchQuote={patchQuote} flushQuotePatch={flushQuotePatch} users={users} />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={3}>
                        <Notes readOnly={readOnly} quote={quote} patchQuote={patchQuote} flushQuotePatch={flushQuotePatch} removeTag={removeTag} />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={4}>
                        <Custom readOnly={readOnly}
                            quote={quote}
                            patchQuote={patchQuote}
                            flushQuotePatch={flushQuotePatch}
                            users={users}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={5}>
                        <Tools readOnly={readOnly} onDeliver={handleDeliver} onDeliverNoPricing={handleDeliverNoPricing} onRevision={handleRevision} handleDeliverConfirmation={handleDeliverConfirmation} onCopyToNew={handleOpenCopyModal} onRefreshPricing={handleRefreshPricing} handleOpenDrawingModal={handleOpenDrawingModal} />
                    </CustomTabPanel> 

                    {/* Copy Quote Modal */}
                    <Modal open={copyModal} onClose={handleClose}>
                        <Box sx={modalStyle}>
                            {/* Sticky Header */}
                            <Box sx={{...headerFooterStyle, top: 0, p: 2, borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="h6">Copy Quote To Opportunity</Typography>
                                <IconButton onClick={handleClose} size="small">
                                    <Close />
                                </IconButton>
                            </Box>

                            {/* Body Goes Here */}
                            <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 2 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, m: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Copying creates a quote with a new, automatically assigned number for the selected opportunity.
                                    </Typography>
                                    <Autocomplete
                                        options={copyOpportunityOptions}
                                        value={copyTargetOpportunity}
                                        loading={copyOpportunitiesLoading}
                                        onChange={(event, newValue) => setCopyTargetOpportunity(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Target Opportunity"
                                                helperText={copyOpportunityOptions.length ? "Only opportunities without a linked quote are shown." : "No opportunities are available right now."}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>

                            {/* Sticky Footer */}
                            <Box sx={{ ...headerFooterStyle, bottom: 0, p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button type="submit" variant="contained" color="primary" sx={{ marginTop: "1em" }} onClick={handleCopyQuote}>
                                    Submit
                                </Button>
                            </Box>        

                        </Box>                        
                    </Modal> 

                    {/* Print PDF With Drawing Modal */}
                    <Modal open={drawingModal} onClose={handleCloseDrawingModal}>
                        <Box sx={modalStyle}>
                            {/* Sticky Header */}
                            <Box sx={{...headerFooterStyle, top: 0, p: 2, borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="h6">Convert To PDF With Drawing</Typography>
                                <IconButton onClick={handleCloseDrawingModal} size="small">
                                    <Close />
                                </IconButton>
                            </Box>

                            {/* Body Goes Here */}
                            <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, m: 2, flexWrap: "wrap" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, m: 2, flexWrap: "wrap" }}>
                                            <Button variant="outlined" component="label">
                                                {drawingPdf ? "Replace PDF" : "Attach PDF"}
                                                <input
                                                    hidden
                                                    type="file"
                                                    accept="application/pdf,.pdf"
                                                    onChange={handleSelectDrawingPdf}
                                                />
                                            </Button>

                                            {drawingPdf && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography variant="body2">
                                                        {drawingPdf.name} ({Math.round(drawingPdf.size / 1024)} KB)
                                                    </Typography>
                                                    <Button size="small" color="error" onClick={handleRemoveDrawingPdf}>
                                                        Remove
                                                    </Button>
                                                </Box>
                                            )}

                                            {drawingPdfError && (
                                                <Typography variant="body2" color="error">
                                                    {drawingPdfError}
                                                </Typography>
                                            )}
                                        </Box>
                                </Box>
                            </Box>

                            {/* Sticky Footer */}
                            <Box sx={{ ...headerFooterStyle, bottom: 0, p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button type="submit" variant="contained" color="primary" sx={{ marginTop: "1em" }} onClick={handlePrintWithDrawing}>
                                    Submit
                                </Button>
                            </Box>        

                        </Box>                        
                    </Modal>                     
                </Box>
            )
        }/>
    )
}

export default UniqueQuote
